import React, { useEffect, useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import MockupGenerationError from "../../modals/MockupErrorModal";
import addWatermarkToFile from "../../components/helpers/addWaterMark";
import { createEngine } from "../../mockup-engine";

const MAX_FILE_MB = 80;
const MAX_DIMENSION = 3000;

const pixelsToFile = (img, name, type = "image/png") =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext("2d").putImageData(new ImageData(img.data, img.width, img.height), 0, 0);
    canvas.toBlob((blob) => {
      canvas.width = canvas.height = 0;
      blob ? resolve(new File([blob], name, { type })) : reject(new Error("Could not create the preview image."));
    }, type);
  });

const compressImage = async (file, targetMaxEdge = 1000) =>
  imageCompression(file, { maxWidthOrHeight: targetMaxEdge, initialQuality: 0.6, fileType: "image/jpeg", useWebWorker: true });

/**
 * PSD upload for the listing form. Reads and validates the template with the
 * native mockup engine (the same one the editor uses), so sellers learn
 * immediately whether their PSD works. Fills the same form fields as before:
 * file, compressed_file, json_file, Design, canva, canvasWidth, canvasHeight.
 */
const PsdCanvas = ({ handleChange, setLoading, loading }) => {
  const engineRef = useRef(null);
  const [fileSizeMessage, setFileSizeMessage] = useState("");
  const [psdError, setPsdError] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const [stage, setStage] = useState("");

  useEffect(() => {
    engineRef.current = createEngine();
    return () => engineRef.current?.dispose();
  }, []);

  const resetFields = () => {
    for (const key of ["file", "json_file", "compressed_file", "compressed_json_file", "Design", "template_preview", "template_full", "template_thumb", "template_version"]) {
      handleChange(key, null);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPsdError("");
    setWarnings([]);
    if (file.size / (1024 * 1024) > MAX_FILE_MB) {
      setFileSizeMessage(`File size exceeds ${MAX_FILE_MB} MB limit.`);
      return;
    }
    setFileSizeMessage("");
    if (!/\.psd$/i.test(file.name)) {
      setPsdError("Please choose a Photoshop (.psd) file.");
      return;
    }

    const engine = engineRef.current;
    setLoading(true);
    resetFields();
    try {
      await engine.close("upload").catch(() => {});
      setStage("Reading your PSD…");
      const info = await engine.open("upload", { bytes: new Uint8Array(await file.arrayBuffer()), keepSource: true });
      if (info.width > MAX_DIMENSION || info.height > MAX_DIMENSION) {
        setPsdError(`PSD file dimensions exceed ${MAX_DIMENSION}x${MAX_DIMENSION} pixels.`);
        return;
      }

      // Converting the template proves it works in the editor; the result is
      // stored with the listing so buyers' editors open it instantly.
      setStage("Converting your mockup…");
      const packages = await engine.templatePackages("upload", (p, label) => setStage(`${label || "Converting your mockup…"} ${Math.round(p * 100)}%`));
      handleChange("template_preview", new File([packages.preview], "template_preview.mktp", { type: "application/octet-stream" }));
      handleChange("template_full", new File([packages.full], "template_full.mktp", { type: "application/octet-stream" }));
      handleChange("template_thumb", new File([packages.thumb], "template_thumb.mktp", { type: "application/octet-stream" }));
      handleChange("template_version", packages.version);

      setStage("Preparing your listing files…");
      const assets = await engine.uploadAssets("upload");
      const preview = await pixelsToFile(assets.preview, "psd-preview.png");
      handleChange("file", preview);
      handleChange("json_file", new Blob([assets.payload], { type: "text/plain" }));
      handleChange("compressed_json_file", null);
      if (assets.design) handleChange("Design", await pixelsToFile(assets.design, "Design.png"));

      const compressed = await compressImage(preview);
      handleChange("compressed_file", await addWatermarkToFile(compressed));

      const { width, height } = info;
      handleChange("canvasWidth", width);
      handleChange("canvasHeight", height);
      const downScale = (window.innerWidth <= 630 ? 300 : 600) / width;
      handleChange("canva", {
        pixelRatio: (width + height) / 2 / 500,
        width,
        height,
        canvasWidth: Math.floor(width * downScale),
        canvasHeight: Math.floor(height * downScale),
        whiteLayer: false,
        commontDownScaleRatio: downScale,
        designWidth: info.designWidth,
        designHeight: info.designHeight,
      });
      setWarnings([...new Set([...(info.warnings || []), ...(packages.warnings || [])])]);
    } catch (error) {
      console.error("PSD processing failed", error);
      resetFields();
      setErrorModal({ show: true, message: error?.message || "" });
    } finally {
      setStage("");
      setLoading(false);
      engine.close("upload").catch(() => {});
    }
  };

  return (
    <div>
      <div className="form-group w-100 mb-3">
        <label style={{ backgroundColor: "#DBD9D4", cursor: loading ? "progress" : "pointer" }} className="p-3 w-100 rounded-4" htmlFor="file">
          {loading ? stage || "Upload File..." : "Upload File"}
        </label>
        <input hidden id="file" type="file" accept=".psd,image/vnd.adobe.photoshop" onChange={handleFileChange} disabled={loading} />
        {fileSizeMessage && <p className="text-danger text-start">{fileSizeMessage}</p>}
        {psdError && <p className="text-danger text-start">{psdError}</p>}
        {warnings.length > 0 && (
          <ul className="text-start small text-muted mt-2 mb-0 ps-3">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        )}
      </div>
      <MockupGenerationError showModal={errorModal.show} message={errorModal.message} modalClose={() => setErrorModal({ show: false, message: "" })} />
    </div>
  );
};

export default PsdCanvas;
